import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/shared/store/authStore';
import { loginWithEmail, getIdToken } from '@/shared/services/authService';
import { apiClient } from '@/shared/services/api';
import { uploadImage, generateUserImagePath } from '@/shared/services/storage';
import { ImageUpload } from '@/shared/components/ImageUpload';
import { registerSchema, type RegisterFormData } from '../schemas/auth.schemas';
import { z } from 'zod';
import styles from './RegisterPage.module.css';

export function RegisterPage() {
  const { setAuth } = useAuthStore();
  const [formData, setFormData] = useState<Omit<RegisterFormData, 'photoUrl'>>({
    email: '',
    password: '',
    fullName: '',
    phone: '',
  });
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterFormData, string>>>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateField = (field: keyof typeof formData, value: string): string | undefined => {
    try {
      const fieldSchema = registerSchema.shape[field];
      fieldSchema.parse(value);
      return undefined;
    } catch (err) {
      if (err instanceof z.ZodError) {
        return err.errors[0]?.message;
      }
      return 'Valor inválido';
    }
  };

  const handleBlur = (field: keyof typeof formData) => () => {
    const value = formData[field];
    const error = validateField(field, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrors({});

    // Validar todo el formulario (sin photoUrl por ahora, se agrega después)
    const validationResult = registerSchema.safeParse({
      ...formData,
      photoUrl: 'https://placeholder.com', // Placeholder temporal para validación
    });
    if (!validationResult.success) {
      const fieldErrors: Partial<Record<keyof RegisterFormData, string>> = {};
      validationResult.error.errors.forEach((err) => {
        const field = err.path[0] as keyof RegisterFormData;
        if (field && field !== 'photoUrl') {
          // No mostrar error de photoUrl aquí, se maneja con el componente ImageUpload
          fieldErrors[field] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    // Validar que se haya seleccionado una foto
    if (!photoFile) {
      setError('Por favor selecciona una foto de perfil');
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Subir la imagen a Firebase Storage primero
      const imagePath = generateUserImagePath();
      const photoUrl = await uploadImage(photoFile, imagePath);

      // Preparar datos para el registro (sin photoUrl en el schema, lo agregamos manualmente)
      const registerData = {
        ...validationResult.data,
        photoUrl: photoUrl,
      };

      // Llamar al endpoint del backend para registrar
      const response = await apiClient.post('/auth/register', registerData);

      if (response.data) {
        // El backend ya creó el usuario en Firebase Auth, ahora solo iniciamos sesión
        // Esperar un momento para que Firebase procese el usuario
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Iniciar sesión automáticamente con las credenciales
        const userCredential = await loginWithEmail(formData.email, formData.password);
        const firebaseUser = userCredential.user;

        // Obtener token
        const token = await getIdToken(true); // Forzar refresh para obtener el token actualizado
        if (!token) {
          throw new Error('No se pudo obtener el token de autenticación');
        }

        // El usuario se registra como 'user', se cambia manualmente a 'admin' después en Firebase Console
        setAuth(firebaseUser, 'user');
        setSuccess(true);

        // Mostrar mensaje de éxito con instrucciones
        // El usuario debe cambiar el rol manualmente antes de poder usar el dashboard como admin
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string; error?: string } } };
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          'Error al registrar. Intenta nuevamente.';
        setError(errorMessage);
      } else {
        setError('Error al registrar. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.successMessage}>
            <h2 className={styles.successTitle}>¡Registro Exitoso!</h2>
            <p className={styles.successText}>
              Tu cuenta ha sido creada. Ahora debes cambiar tu rol a <strong>admin</strong> en Firebase Console.
            </p>
            <div className={styles.instructions}>
              <h3>Pasos para activar como Admin:</h3>
              <ol>
                <li>Ve a <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer">Firebase Console</a></li>
                <li>Selecciona el proyecto <strong>encoreeats-ca131</strong></li>
                <li>Ve a <strong>Authentication</strong> → <strong>Users</strong></li>
                <li>Encuentra tu usuario: <strong>{formData.email}</strong></li>
                <li>Haz clic en los tres puntos (⋮) y selecciona <strong>Edit</strong></li>
                <li>En <strong>Custom claims</strong>, agrega: <code>{`{"role": "admin"}`}</code></li>
                <li>Guarda los cambios</li>
                <li>Cierra sesión y vuelve a iniciar sesión para actualizar el token</li>
              </ol>
            </div>
            <div className={styles.actions}>
              <Link to="/login" className={styles.button}>
                Ir a Iniciar Sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>EncoreEats</h1>
          <p className={styles.subtitle}>Registro de Administrador</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.field}>
            <label htmlFor="fullName">Nombre Completo</label>
            <input
              id="fullName"
              type="text"
              value={formData.fullName}
              onChange={handleChange('fullName')}
              onBlur={handleBlur('fullName')}
              placeholder="Juan Pérez"
              required
              autoComplete="name"
            />
            {errors.fullName && <span className={styles.fieldError}>{errors.fullName}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              placeholder="admin@encoreeats.com"
              required
              autoComplete="email"
            />
            {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="phone">Teléfono</label>
            <input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange('phone')}
              onBlur={handleBlur('phone')}
              placeholder="+591 70000000"
              required
              autoComplete="tel"
            />
            {errors.phone && <span className={styles.fieldError}>{errors.phone}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={formData.password}
              onChange={handleChange('password')}
              onBlur={handleBlur('password')}
              placeholder="Mínimo 6 caracteres"
              required
              autoComplete="new-password"
            />
            {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
          </div>

          <ImageUpload
            onChange={setPhotoFile}
            error={errors.photoUrl}
            label="Foto de Perfil"
          />

          <button type="submit" className={styles.button} disabled={loading}>
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>

          <div className={styles.footer}>
            <p>
              ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

